import { ExclamationIcon } from "@heroicons/react/outline";
import { InfoCircledIcon } from "@radix-ui/react-icons";
import { forwardRef } from "react";

import { cn } from "@/lib/utils";

interface BannerProps extends React.HTMLAttributes<HTMLDivElement> {
  type?: "warning" | "error" | "info";
  icon?: React.ReactNode;
  prefix?: string;
  suffix?: React.ReactNode;
  children: React.ReactNode;
}

const Banner = forwardRef<HTMLDivElement, BannerProps>(
  ({ type = "warning", icon, prefix, suffix, children, className, ...props }, ref) => {
    const styles = {
      warning: "bg-amber-50 border-amber-200 text-amber-800 shadow-sm",
      error: "bg-red-50 border-red-200 text-red-800 shadow-sm",
      info: "bg-blue-50 border-blue-200 text-blue-800 shadow-sm",
    };

    const defaultIcons = {
      warning: <ExclamationIcon className="h-5 w-5 text-amber-500" />,
      error: <ExclamationIcon className="h-5 w-5 text-red-500" />,
      info: <InfoCircledIcon className="h-5 w-5 text-blue-500" />,
    };

    const defaultPrefixes = {
      warning: "Warning:",
      error: "Error:",
      info: "Info:",
    };

    return (
      <div
        ref={ref}
        className={cn(
          "flex items-center gap-3 rounded-lg border px-4 py-3 transition-colors",
          styles[type],
          className,
        )}
        {...props}
      >
        <span className="flex-shrink-0">{icon || defaultIcons[type]}</span>
        <div className="text-sm font-medium">
          {prefix ?? defaultPrefixes[type]} {children}
        </div>
        {suffix && <div className="ml-auto">{suffix}</div>}
      </div>
    );
  },
);

Banner.displayName = "Banner";

export default Banner;
